import { FormValidationStatus } from '@customTypes/FormValidationStatus'
import clsx from 'clsx'
import React, {
  useImperativeHandle,
  useRef,
  type PropsWithChildren,
} from 'react'
import { IoChevronDown } from 'react-icons/io5'
import s from './Select.module.scss'

interface Props {
  placeholder?: string
  validationStatus?: FormValidationStatus
}
type NativeAttrs = Omit<React.ComponentPropsWithoutRef<'select'>, keyof Props>

export type SelectProps = Props & NativeAttrs

const Select = React.forwardRef<
  HTMLSelectElement,
  PropsWithChildren<SelectProps>
>(
  (
    {
      className,
      placeholder,
      validationStatus,
      disabled,
      required,
      children,
      ...props
    },
    forwardedRef,
  ) => {
    // Handle ref forwarding
    const ref = useRef<HTMLSelectElement>(null)
    useImperativeHandle(forwardedRef, () => ref.current)

    return (
      <span className={clsx(className, s.selectWrapper)}>
        <select
          ref={ref}
          required={required}
          disabled={disabled}
          aria-invalid={validationStatus === 'error' ? 'true' : undefined}
          defaultValue={placeholder ?? undefined}
          className={s.select}
          {...props}
        >
          {placeholder && (
            <option value="" disabled={required} hidden={required}>
              {placeholder}
            </option>
          )}
          {children}
        </select>
        <IoChevronDown className={s.selectIcon} />
      </span>
    )
  },
)

const Option: React.FC<
  React.PropsWithChildren<
    React.HTMLProps<HTMLOptionElement> & { value: string }
  >
> = (props) => <option {...props} />

Select.displayName = 'Select'
export default Object.assign(Select, { Option })
